import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { createLpInvestmentSuccessRoute } from 'adminConstants/routes';
import Button from 'components/Button';
import CurrencyRate from 'components/CurrencyRate';
import AllocationsPreview from 'components/new/AllocationsPreview';
import FullHeightView from 'components/new/FullHeightView';
import PageLoader from 'components/PageLoader';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import { ActionRef, ActionStatus } from 'further-types/lp/action';
import { InvestmentTypes } from 'further-types/lp/investment';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import { useDisclosure } from 'further-ui/hooks';
import { AllocationRuleLabels, FundingSourceLabels } from 'further-ui/labels';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { investment as rules } from 'further-ui/validations';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useActionTransactions from 'hooks/lp/ui/transactions/useActionTransactions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import ReviewRevaluation from 'routes/LpPages/Company/Revaluations/ReviewRevaluation';
import useHoldingClasses from '../hooks/useHoldingClasses';

type ApproveTransactionProps = {
  onCancel: () => void;
  selectedVehicle: {
    _id: string;
    vehicleName: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
};

const ApproveTransaction: React.FC<ApproveTransactionProps> = ({
  onCancel,
  selectedVehicle,
}) => {
  const history = useHistory();
  const notification = useNotification();
  const apiUtils = api.useUtils();
  const { hasEqualisationInDraftState } = useEqualisations(selectedVehicle._id);
  const confirmDialog = useDisclosure();

  const { control, watch, setValue, getValues, handleSubmit, setError } =
    useFormContext<rules.CreateInvestmentFormValues>();

  const {
    calculateTotalInvestmentAmount,
    calculateAverageUnitPrice,
    calculateTotalUnits,
    getCurrency,
  } = useHoldingClasses({
    control,
    watch,
    setValue,
    getValues,
    setError,
  });
  const totalInvestmentAmountTcy = calculateTotalInvestmentAmount();
  const currency = getCurrency();

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const ledgerDate =
    getValues('dealCompletionDate') ?? new CalendarDay(new Date());

  const { fxRate, changeRate, isFetchingRate } = useCurrencyRate({
    vehicleId: selectedVehicle._id,
    currency,
    date: ledgerDate,
    defaultCurrencyRate: getValues('currencyRate') ?? null,
    onRateChange: (rate) => {
      setValue('currencyRate', rate ?? undefined);
    },
  });

  const transactions = useActionTransactions({
    vehicleId: selectedVehicle._id,
    action: {
      actionRef: ActionRef.Investment,
      actionType: InvestmentTypes.InvestmentCreated,
      amountIncVatTcy: Math.abs(totalInvestmentAmountTcy),
      amountExVatTcy: Math.abs(totalInvestmentAmountTcy),
      currency,
    },
    transactions: watch('transactions'),
  });

  const mutationOptions = useHandleTrpcMutation()({
    onSuccess: () => {
      notification.success('Investment saved successfully.');
      apiUtils.lpInvestment.invalidate();
    },
    onError: (error) => {
      notification.error(error.message || 'Failed to approve investment.');
    },
  });

  const upsertInvestmentMutation =
    api.lpInvestment.upsert.useMutation(mutationOptions);

  const onTransactionChange = (transactions: Array<Transaction>) => {
    setValue(
      'transactions',
      transactions.map((t) => ({
        ...t,
        id: t.id?.toString(),
        amountExVatLcy: t.amountExVatLcy ?? 0,
        amountIncVatLcy: t.amountIncVatLcy ?? 0,
        vatRate: 0,
      })),
    );
  };

  const onSaveDraft = handleSubmit(async (data) => {
    const response = await upsertInvestmentMutation.mutateAsync({
      ...data,
      status: ActionStatus.Draft,
    });

    const investmentId = response.id.toString();
    setValue('investmentId', investmentId);

    setValue(
      'transactions',
      response.transactions?.map((t) => {
        const transactionCurrencyRate = t.transactionCurrencyRate
          ? {
              fetchedRate: t.transactionCurrencyRate.fetchedRate ?? undefined,
              fetchedSource:
                t.transactionCurrencyRate.fetchedSource ?? undefined,
              overrideRate: t.transactionCurrencyRate.overrideRate ?? undefined,
              overrideSource:
                t.transactionCurrencyRate.overrideSource ?? undefined,
            }
          : undefined;
        return {
          ...t,
          transactionCurrencyRate,
          transactionTypeId: t.transactionTypeId.toString(),
          actionId: t.sourceActionId?.toString(),
          operationType: t.operationType,
        };
      }),
    );
  });

  const onSubmit: SubmitHandler<rules.CreateInvestmentFormValues> = async (
    data,
  ) => {
    const fundingSource = getValues('fundingSource');
    if (!fundingSource) {
      notification.error('Funding source is required.');
      return;
    }

    await upsertInvestmentMutation.mutateAsync({
      ...data,
      status: ActionStatus.Approved,
    });

    const hasNewAsset = !!getValues('createdNewAsset');
    const hasNewHoldingClass = !!getValues('createdNewHoldingClass');
    history.push(
      createLpInvestmentSuccessRoute(
        getValues('assetId'),
        getValues('fundingSource'),
        hasNewAsset && hasNewHoldingClass,
      ),
    );
  };

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  if (!transactionTypeOptions.length) {
    return (
      <Typography>
        To set up account mappings, please first create some transaction types.
      </Typography>
    );
  }

  const newRevaluations = getValues('newRevaluations');
  const investmentId = watch('investmentId');

  return (
    <FullHeightView
      actionBar={
        <Row justify="space-between" centered spacing="sm">
          <Button variant="outlined" onClick={onCancel} size="large">
            Previous step
          </Button>
          <Row centered spacing="sm">
            <Button
              variant="outlined"
              loading={upsertInvestmentMutation.isPending}
              onClick={onSaveDraft}
              size="large"
            >
              Save draft
            </Button>
            <Button
              variant="contained"
              color="primary"
              loading={upsertInvestmentMutation.isPending}
              onClick={
                hasEqualisationInDraftState
                  ? confirmDialog.onOpen
                  : handleSubmit(onSubmit, () => {
                      notification.error('Please fill in all required fields.');
                    })
              }
              size="large"
            >
              Save and approve
            </Button>
          </Row>
        </Row>
      }
    >
      <form>
        <Column spacing="lg">
          <StaticField label="Vehicle">
            {selectedVehicle.vehicleName}
          </StaticField>
          <StaticField label="Asset">{getValues('asset.name')}</StaticField>
          <StaticField label="Deal name">{getValues('dealName')}</StaticField>
          <StaticField label="Deal completion date">
            {getValues('dealCompletionDate')?.toLabel()}
          </StaticField>
          <StaticField label="Holding classe(s)">
            {getValues('holdingClasses')
              .map((holdingClass) => holdingClass.name)
              .join(', ')}
          </StaticField>
          <StaticField label="Total investment amount">
            {numberToCurrencyString(totalInvestmentAmountTcy, {
              currency,
            })}
          </StaticField>
          <StaticField label="Units">
            {numberToDisplayString(calculateTotalUnits())}
          </StaticField>
          <StaticField label="Average unit price">
            {numberToCurrencyString(calculateAverageUnitPrice(), {
              currency,
              unlimitedDp: true,
            })}
          </StaticField>
          <StaticField label="Allocation rule">
            {AllocationRuleLabels[getValues('allocationRule') ?? '']}
          </StaticField>
          <StaticField label="Funding source">
            {FundingSourceLabels[getValues('fundingSource') ?? '']}
          </StaticField>

          <Divider />
          <Typography variant="h5">Review allocations</Typography>
          <AllocationsPreview
            vehicleId={selectedVehicle._id}
            allocations={getValues('allocations') ?? []}
            baseCurrency={selectedVehicle.baseCurrency}
            currency={currency}
            allocatedActionLabel="Investment"
            currentActionIds={investmentId ? [investmentId] : undefined}
            date={ledgerDate}
          />
          <Divider />
          <Typography variant="h5">Review account transactions</Typography>

          <Column spacing="xs">
            <CurrencyRate
              currency={currency}
              date={ledgerDate}
              vehicle={selectedVehicle}
              fxRate={fxRate}
              onRateChange={changeRate}
              isFetchingRate={isFetchingRate}
            />
            <TransactionAllocator
              transactionTypeOptions={transactionTypeOptions}
              transactions={transactions}
              onChange={onTransactionChange}
              transactionCurrency={currency}
              fxRate={fxRate}
              vehicle={selectedVehicle}
              transactionsInFuture={ledgerDate.isInFuture()}
            />
          </Column>

          {newRevaluations && newRevaluations.length > 0 && (
            <>
              <Divider />
              <ReviewRevaluation
                vehicle={selectedVehicle}
                assetName={getValues('asset.name')}
                assetId={getValues('assetId')}
                revaluationDate={ledgerDate}
              />
            </>
          )}
        </Column>
      </form>

      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={handleSubmit(onSubmit, () => {
            notification.error('Please fill in all required fields.');
          })}
          loading={upsertInvestmentMutation.isPending}
        />
      )}
    </FullHeightView>
  );
};

export default ApproveTransaction;
